import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ServerTimePipe } from '@shared/pipes/server-time.pipe';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { InterventionContextSummary, InterventionTransactionHistoryResponse } from '../../../intervention.model';

// How many entries of the audit trail the side panel shows; the History tab has all of them.
const AUDIT_ENTRIES_SHOWN = 6;

// The two panels beside the steps of the case: the Live context summary (who the case is for and where it stands) and the Governance audit trail
// (the latest things done on the case, with who did them).
@Component({
  selector: 'wizard-side-panels',
  imports: [DatePipe, ServerTimePipe, ...SharedMaterialUIModule],
  templateUrl: './wizard-side-panels.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WizardSidePanelsComponent {
  // What the Live context summary shows; null until it has been loaded.
  readonly context = input<InterventionContextSummary | null>(null);
  readonly history = input<InterventionTransactionHistoryResponse[]>([]);
  // The user asked for the whole trail (the History tab).
  readonly openHistory = output<void>();

  // The summary as label and value rows; a line that is not known yet says so.
  protected readonly rows = computed(() => {
    const context = this.context();
    return [
      { label: 'Case owner / member', value: context?.memberName ? `${context.memberName}${context.userType ? ' (' + context.userType + ')' : ''}` : null },
      { label: 'Customer', value: context?.customer ?? null },
      { label: 'Risk rating', value: context?.riskRating ?? null },
      { label: 'Eligibility', value: context ? eligibilityLabel(context.eligibilityStatus) : null },
      { label: 'Primary clinical issue', value: context?.primaryProblem ?? null },
      { label: 'Selected provider', value: context?.selectedProvider ?? null },
    ];
  });

  // The newest entries first.
  protected readonly entries = computed(() =>
    [...this.history()].sort((a, b) => b.changedDate.localeCompare(a.changedDate)).slice(0, AUDIT_ENTRIES_SHOWN),
  );
}

function eligibilityLabel(status: string): string {
  switch (status) {
    case 'Eligible':
      return 'Verified';
    case 'NotEligible':
      return 'Not eligible';
    default:
      return 'Pending';
  }
}
